/**
 * Governed follow-on lifecycle modals for Private Credit and Leveraged Loans —
 * the PC/LL Manual Parity (WS2) counterpart to
 * ReDebtActionModals. Mirrors that file 1:1 so the lifecycle surface is
 * consistent across credit asset classes.
 *
 * Each modal hits a PrivateCreditModuleController endpoint, keyed by the silo
 * loan PK ({@code loanRecordId}) the dashboard resolves from the position's
 * security_id:
 *   - Paydown / Drawdown / Rate Reset → POST /api/private-credit/loans/{id}/apply-notice
 *   - Amend                           → POST /api/private-credit/loans/{id}/amend
 *   - Writedown                       → POST /api/private-credit/loans/{id}/writedown
 *   - Payoff                          → POST /api/private-credit/loans/{id}/mature
 *   - Record Covenant Test            → POST /api/credit/compliance-certificates/process
 *   - PC Interest / Fee Receipt        → POST /api/private-credit/loans/{id}/interest-settlement
 *
 * Every endpoint drives the SAME services the document-ingestion path uses, so
 * a manual lifecycle event is indistinguishable downstream from a parsed one
 * (PRD 229395, AC2/AC6). Per feedback_position_anchored_dispose the trade
 * ticket is BUY-only — all lifecycle events originate here.
 *
 * LL Paydown and Drawdown remain facility-level borrower events. Their modals
 * use the permissioned tranche-event preview/apply endpoints and distribute to
 * settled holders; they never call the portfolio-scoped PC notice writer.
 *
 * R4-7: the LL Rate Reset collects the INDEX FIXING in percent (sent as
 * `indexFixingPct` to POST /api/credit/loans/{id}/rate-reset, which writes an
 * effective-dated ll_tranche_terms row); the all-in is derived server-side.
 * Future-dated LL fixings are refused with 409. PC keeps the all-in-rate
 * semantics of /api/private-credit/loans/{id}/apply-notice.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { getTodayDate } from '../../utils/dateUtils';
import axios from 'axios';
import {
  ArrowDownCircle, ArrowUpCircle, RefreshCw, AlertTriangle, Banknote, ClipboardCheck, Plus, Trash2, Percent, Layers,
} from 'lucide-react';
import {
  ModalShell, Body, FieldRow, Field, Label, Input, Select, Footer, Button, ErrorBanner,
} from '../realestate/reModalShared';
import { parseAmountShorthand, formatAmountWithCommas } from '../../utils/amountUtils';
import LoanAmendmentWorkspace from './shared/LoanAmendmentWorkspace';

import { DateField } from '../shared/DateField';

export interface CreditPositionLite {
  /** Loan master PK (dim_private_credit_loan.id or dim_leveraged_loan.id). */
  loanRecordId: number;
  securityId: number;
  portfolioId?: number;
  cusip?: string;
  loanName: string;
  borrowerName?: string;
  fundedAmount?: number;
  currentCommitment?: number;
  unfundedAmount?: number;
  reborrowable?: boolean;
  spreadBps?: number;
  allInRate?: number;
  baseRate?: string;
  floorRate?: number;
  csaBps?: number;
  currentIndexFixingPct?: number;
  maturityDate?: string;
  status?: string;
  /** Facility type (dim_private_credit_loan.facility_type) — selects DDTL vs
   *  generic drawdown behaviour. */
  facilityType?: string;
  /** DDTL availability (delayed-draw only): committed / funded / undrawn tranche
   *  amounts + the draw-window expiry. Drive the modal's meter + window guard. */
  ddtlCommitment?: number;
  ddtlFunded?: number;
  ddtlUndrawn?: number;
  ddtlExpiry?: string;
  /** Canonical LL facility-reference balances. These are global borrower
   * amounts, never the selected portfolio's IBOR exposure. */
  globalFacilityOutstanding?: number;
  globalFacilityAvailability?: number;
  drawWindowEnd?: string;
}

interface BaseProps {
  position: CreditPositionLite;
  /** Which credit asset class — selects the backend endpoint family. */
  assetKind: 'PC' | 'LL';
  onClose: () => void;
  onSuccess: () => void;
  /** F-PCP-044: what the person already stated (an Ask Axiom "prepare" chip, a queue action) — prefilled, still reviewed. */
  initial?: { amount?: string; evidenceRef?: string };
}

const today = () => getTodayDate();

/** Base path for the loan-keyed lifecycle endpoints by asset class. */
const loanBase = (assetKind: 'PC' | 'LL') =>
  assetKind === 'LL' ? '/api/credit/loans' : '/api/private-credit/loans';

const covenantPath = (assetKind: 'PC' | 'LL', securityId: number) =>
  assetKind === 'LL'
    ? `/api/credit/loans/by-security/${securityId}/covenants`
    : `/api/private-credit/by-security/${securityId}/covenants`;

const fmtCcy = (n: number | undefined) =>
  n == null ? '$0' : `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const subtitleFor = (p: CreditPositionLite) =>
  `${p.loanName}${p.borrowerName ? ` • ${p.borrowerName}` : ''}`;

const errMsg = (err: any, fallback: string) =>
  err?.response?.data?.error || err?.message || fallback;

// Monetary inputs accept fixed-income shorthand (5M = 5,000, 5MM = 5,000,000,
// 5MMM = 5bn) via the same parser the trade tickets use. Rate/bps fields do NOT.
const parseAmt = (raw: string): number => parseAmountShorthand(raw);

/** Echoes the parsed dollar value under an amount input when shorthand is used. */
const AmountHint: React.FC<{ raw: string }> = ({ raw }) => {
  if (!raw.trim() || !/m/i.test(raw)) return null;
  const n = parseAmountShorthand(raw);
  if (n <= 0) return null;
  return (
    <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 4 }}>
      = ${formatAmountWithCommas(n)}
    </div>
  );
};

/** Amber note when an ops event is future-dated: it parks, it does not apply today. */
const FutureEffectiveNote: React.FC<{ effectiveDate: string; verb: string }> = ({ effectiveDate, verb }) => {
  if (!effectiveDate || effectiveDate <= today()) return null;
  return (
    <div style={{
      background: 'rgba(245, 158, 11, 0.06)', border: '1px solid rgba(245, 158, 11, 0.25)',
      borderRadius: 8, padding: '0.6rem 0.9rem', fontSize: '0.76rem', color: '#fcd34d',
    }}>
      Effective date is in the future — the {verb} will be parked as{' '}
      <strong>PENDING_EFFECTIVE</strong> (no facility, position or cash impact today) and
      auto-applied on {effectiveDate} by the 05:15 ET scheduler.
    </div>
  );
};

const ACTION_TONE = {
  paydown: {
    background: 'rgba(16, 185, 129, 0.06)',
    border: 'rgba(16, 185, 129, 0.22)',
    color: '#6ee7b7',
    detail: '#a7f3d0',
  },
  drawdown: {
    background: 'rgba(99, 102, 241, 0.06)',
    border: 'rgba(99, 102, 241, 0.25)',
    color: '#c7d2fe',
    detail: '#ddd6fe',
  },
  rate: {
    background: 'rgba(59, 130, 246, 0.06)',
    border: 'rgba(59, 130, 246, 0.25)',
    color: '#93c5fd',
    detail: '#bfdbfe',
  },
  amend: {
    background: 'rgba(139, 92, 246, 0.06)',
    border: 'rgba(139, 92, 246, 0.25)',
    color: '#c4b5fd',
    detail: '#ddd6fe',
  },
  covenant: {
    background: 'rgba(34, 211, 238, 0.06)',
    border: 'rgba(34, 211, 238, 0.25)',
    color: '#67e8f9',
    detail: '#a5f3fc',
  },
  warning: {
    background: 'rgba(245, 158, 11, 0.06)',
    border: 'rgba(245, 158, 11, 0.25)',
    color: '#fcd34d',
    detail: '#fde68a',
  },
} as const;

const LifecycleNotice: React.FC<{
  tone: keyof typeof ACTION_TONE;
  title: string;
  children: React.ReactNode;
}> = ({ tone, title, children }) => {
  const t = ACTION_TONE[tone];
  return (
    <div style={{
      background: t.background, border: `1px solid ${t.border}`,
      borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.8rem', color: t.color,
    }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{title}</div>
      <div style={{ color: t.detail }}>{children}</div>
    </div>
  );
};

const MetricGrid: React.FC<{ children: React.ReactNode; columns?: number }> = ({ children, columns = 2 }) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
    gap: '1rem',
  }}>
    {children}
  </div>
);

const Metric: React.FC<{ label: string; value: React.ReactNode; color?: string }> = ({
  label, value, color = '#cbd5e1',
}) => (
  <Field>
    <Label>{label}</Label>
    <div style={{ color, fontWeight: 700 }}>{value}</div>
  </Field>
);

const PreviewCard: React.FC<{
  tone: keyof typeof ACTION_TONE;
  ok?: boolean;
  title: string;
  children: React.ReactNode;
}> = ({ tone, ok = true, title, children }) => {
  const t = ACTION_TONE[tone];
  return (
    <div style={{
      marginTop: 4, borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.78rem',
      background: ok ? t.background : 'rgba(239, 68, 68, 0.06)',
      border: `1px solid ${ok ? t.border : 'rgba(239,68,68,0.25)'}`,
      color: ok ? t.color : '#fca5a5',
    }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{title}</div>
      <div style={{ color: '#cbd5e1' }}>{children}</div>
    </div>
  );
};

const CovenantRowCard: React.FC<{
  row: CovTestRow;
  index: number;
  canRemove: boolean;
  onChange: (patch: Partial<CovTestRow>) => void;
  onRemove: () => void;
}> = ({ row, index, canRemove, onChange, onRemove }) => (
  <div style={{
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: 8,
    padding: '0.75rem',
    display: 'grid',
    gap: 10,
    background: 'rgba(15, 23, 42, 0.45)',
  }}>
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 8, alignItems: 'end' }}>
      <Field>
        <Label>Covenant</Label>
        <Input type="text" value={row.covenantName}
          onChange={e => onChange({ covenantName: e.target.value })} placeholder="e.g. Total Net Leverage Ratio" />
      </Field>
      <Button onClick={onRemove} disabled={!canRemove}
        title="Remove covenant row" style={{ padding: '0 0.6rem', height: 40 }}><Trash2 size={14} /></Button>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 8 }}>
      <Field>
        <Label>Numerator / Value</Label>
        <Input type="text" inputMode="decimal" value={row.numerator}
          onChange={e => onChange({ numerator: e.target.value })} placeholder="e.g. 4.24 or 416.4MM" />
      </Field>
      <Field>
        <Label>Denominator</Label>
        <Input type="text" inputMode="decimal" value={row.denominator}
          onChange={e => onChange({ denominator: e.target.value })} placeholder="optional" />
      </Field>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '0.45fr minmax(0, 1fr)', gap: 8 }}>
      <Field>
        <Label>Test</Label>
        <Select value={row.operator} onChange={e => onChange({ operator: e.target.value })}>
          <option value="<=">≤</option>
          <option value=">=">≥</option>
        </Select>
      </Field>
      <Field>
        <Label>Limit</Label>
        <Input type="text" inputMode="decimal" value={row.threshold}
          onChange={e => onChange({ threshold: e.target.value })} placeholder="threshold" />
      </Field>
    </div>
    <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
      Row {index + 1}: server recomputes actual, cushion, pass/fail, and evidence lineage.
    </div>
  </div>
);

// DDTL tranche availability: a drawn/undrawn bar (with the pending draw shown as
// a lighter overlay on the undrawn portion), the undrawn headline, and a
// days-to-expiry countdown that turns amber/red as the draw window closes.
const DdtlAvailabilityMeter: React.FC<{
  commitment: number; funded: number; undrawn: number;
  drawAmount: number; expiry?: string; daysToExpiry: number | null;
}> = ({ commitment, funded, undrawn, drawAmount, expiry, daysToExpiry }) => {
  const total = commitment > 0 ? commitment : Math.max(1, funded + undrawn);
  const fundedPct = Math.min(100, (funded / total) * 100);
  const drawPct = Math.min(100 - fundedPct, (Math.min(drawAmount, undrawn) / total) * 100);
  const undrawnPct = Math.max(0, 100 - fundedPct - drawPct);
  const expiryColor = daysToExpiry == null ? '#94a3b8'
    : daysToExpiry < 0 ? '#f87171' : daysToExpiry <= 30 ? '#fbbf24' : '#34d399';
  const expiryText = daysToExpiry == null ? '—'
    : daysToExpiry < 0 ? 'window closed' : daysToExpiry === 0 ? 'expires today' : `expires in ${daysToExpiry}d`;
  return (
    <div style={{
      background: 'rgba(245, 158, 11, 0.05)', border: '1px solid rgba(245, 158, 11, 0.22)',
      borderRadius: 8, padding: '0.7rem 0.9rem',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: '0.72rem', color: '#fcd34d', fontWeight: 700, letterSpacing: '0.03em' }}>
          DDTL AVAILABILITY
        </span>
        <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
          {fmtCcy(funded)} drawn / {fmtCcy(total)}
        </span>
      </div>
      <div style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', background: '#1e293b' }}>
        <div style={{ width: `${fundedPct}%`, background: '#f59e0b' }} title="Drawn" />
        <div style={{ width: `${drawPct}%`, background: '#fcd34d' }} title="This draw" />
        <div style={{ width: `${undrawnPct}%`, background: '#334155' }} title="Undrawn" />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
        <span style={{ fontSize: '0.74rem', color: '#e2e8f0' }}>
          Undrawn <strong style={{ color: '#fcd34d' }}>{fmtCcy(undrawn)}</strong>
        </span>
        <span style={{ fontSize: '0.74rem', color: expiryColor, fontWeight: 600 }}>
          {expiry ? `${expiry} · ${expiryText}` : expiryText}
        </span>
      </div>
    </div>
  );
};

// ============ LL FACILITY-LEVEL PAYDOWN (ops tranche-event, FRD §5 / FR-9) ============
//
// A leveraged-loan paydown is a FACILITY-level borrower event: one global
// tranche event + pro-rata (or investor-explicit) child applications per holder
// (FRD D7/FR-9). It is NOT a portfolio-scoped action — so this modal posts to
// the permissioned ops tranche-event endpoint, and first previews the split
// (server-computed) before the operator confirms (FRD §5 "preview before
// application shows affected portfolios, calculations, and exceptions").

interface PaydownPreviewHolder {
  portfolioId: number;
  settledFunded: number;
  appliedAmount: number;
  postFunded: number;
  sharePct: number;
  investorStated: boolean;
}
interface PaydownPreviewResult {
  ok: boolean;
  message: string;
  facilityAmount: number;
  globalOutstandingBefore: number;
  globalOutstandingAfter: number;
  holders: PaydownPreviewHolder[];
}

const LL_TRANCHE_EVENTS_BASE = '/api/credit/ll/tranche-events';

const LlFacilityPaydownModal: React.FC<BaseProps> = ({ position, onClose, onSuccess, initial }) => {
  const [amount, setAmount] = useState(initial?.amount || '');
  const [effectiveDate, setEffectiveDate] = useState(today());
  const [evidenceRef, setEvidenceRef] = useState(initial?.evidenceRef || '');
  const [preview, setPreview] = useState<PaydownPreviewResult | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Any input change invalidates a stale preview so Confirm can't apply figures
  // the operator didn't just see.
  const resetPreview = () => setPreview(null);

  const payload = () => ({
    facilityAmount: parseAmt(amount),
    effectiveDate,
    evidenceRef: evidenceRef.trim() || null,
  });

  const runPreview = async () => {
    setError(null);
    const n = parseAmt(amount);
    if (!n || n <= 0) { setError('Facility paydown amount must be > 0'); return; }
    setPreviewing(true);
    try {
      const resp = await axios.post(
        `${LL_TRANCHE_EVENTS_BASE}/tranches/${position.loanRecordId}/paydown/preview`, payload());
      setPreview(resp.data as PaydownPreviewResult);
    } catch (err: any) {
      setError(errMsg(err, 'Failed to preview paydown'));
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  };

  const submit = async () => {
    setError(null);
    if (!evidenceRef.trim()) { setError('Agent evidence reference is required'); return; }
    setSubmitting(true);
    try {
      await axios.post(
        `${LL_TRANCHE_EVENTS_BASE}/tranches/${position.loanRecordId}/paydown`, payload());
      onSuccess();
    } catch (err: any) {
      setError(errMsg(err, 'Failed to record facility paydown'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="Record Paydown"
      subtitle={subtitleFor(position)}
      icon={<ArrowDownCircle size={18} className="text-emerald-400" />}
      onClose={onClose}
    >
      <Body>
        <LifecycleNotice tone="paydown" title="Operations action — facility-level paydown">
          Records one borrower-level paydown and distributes it pro-rata across settled holders
          (FRD §5 / FR-9). Requires agent-notice evidence. Preview the holder split before applying.
        </LifecycleNotice>

        <FieldRow>
          <Field>
            <Label>Effective Date</Label>
            <DateField
              value={effectiveDate ?? ''}
              onChange={iso => { setEffectiveDate(iso); resetPreview(); }}
            />
          </Field>
          <Field>
            <Label>Paydown Amount (USD)</Label>
            <Input type="text" inputMode="decimal" value={amount}
              onChange={e => { setAmount(e.target.value); resetPreview(); }} placeholder="e.g. 10MM or 10,000,000" />
            <AmountHint raw={amount} />
          </Field>
        </FieldRow>

        <FutureEffectiveNote effectiveDate={effectiveDate} verb="paydown" />

        <Field>
          <Label>Agent Evidence Ref</Label>
          <Input type="text" value={evidenceRef}
            onChange={e => setEvidenceRef(e.target.value)} placeholder="e.g. BofA-PAYDOWN-HTPK-20260720" />
        </Field>

        {preview && (
          <PreviewCard tone="paydown" ok={preview.ok} title={preview.message}>
            <div style={{ marginBottom: preview.holders.length ? 8 : 0 }}>
              Global outstanding {fmtCcy(preview.globalOutstandingBefore)} →{' '}
              <strong style={{ color: '#e2e8f0' }}>{fmtCcy(preview.globalOutstandingAfter)}</strong>
            </div>
            {preview.holders.map(h => (
              <div key={h.portfolioId} style={{
                display: 'flex', justifyContent: 'space-between', gap: 12,
                color: '#cbd5e1', padding: '2px 0',
              }}>
                <span>Portfolio {h.portfolioId}{h.investorStated ? ' (stated)' : ''}</span>
                <span>
                  {fmtCcy(h.settledFunded)} → <strong style={{ color: '#e2e8f0' }}>{fmtCcy(h.postFunded)}</strong>
                  {'  '}(−{fmtCcy(h.appliedAmount)}, {Number(h.sharePct).toFixed(2)}%)
                </span>
              </div>
            ))}
          </PreviewCard>
        )}

        {error && <ErrorBanner>{error}</ErrorBanner>}
      </Body>
      <Footer>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button onClick={runPreview} disabled={previewing || submitting || !effectiveDate}>
          {previewing ? 'Previewing…' : 'Preview'}
        </Button>
        <Button $primary onClick={submit} disabled={submitting || !preview?.ok || !effectiveDate}>
          {submitting ? 'Applying…' : 'Confirm Paydown'}
        </Button>
      </Footer>
    </ModalShell>
  );
};

// ============ PAYDOWN ============

export const PcPaydownModal: React.FC<BaseProps> = ({ position, assetKind, onClose, onSuccess, initial }) => {
  const [amount, setAmount] = useState(initial?.amount || '');
  const [effectiveDate, setEffectiveDate] = useState(today());
  const [evidenceRef, setEvidenceRef] = useState(initial?.evidenceRef || '');
  const [previewed, setPreviewed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remaining = useMemo(() => {
    const n = parseAmt(amount) || 0;
    return Math.max(0, (position.fundedAmount ?? 0) - n);
  }, [amount, position]);
  const paydownAmount = parseAmt(amount) || 0;
  const commitmentAfter = position.reborrowable
    ? (position.currentCommitment ?? 0)
    : Math.max(0, (position.currentCommitment ?? position.fundedAmount ?? 0) - paydownAmount);
  const unfundedAfter = position.reborrowable
    ? (position.unfundedAmount ?? 0) + paydownAmount
    : Math.max(0, commitmentAfter - remaining);

  if (assetKind === 'LL') {
    // LL paydown is a facility-level ops tranche-event (FRD §5 / FR-9), not a
    // portfolio-scoped modal. Render the permissioned facility paydown ticket
    // (preview + apply via /api/credit/ll/tranche-events), not the old disabled
    // placeholder.
    return <LlFacilityPaydownModal position={position} assetKind={assetKind}
      onClose={onClose} onSuccess={onSuccess} />;
  }

  const resetPreview = () => setPreviewed(false);

  const runPreview = () => {
    setError(null);
    const n = parseAmt(amount);
    if (!n || n <= 0) { setError('Paydown amount must be > 0'); return; }
    const cap = position.fundedAmount ?? 0;
    if (n > cap) { setError(`Paydown exceeds funded balance ${fmtCcy(cap)}`); return; }
    setPreviewed(true);
  };

  const submit = async () => {
    setError(null);
    const n = parseAmt(amount);
    if (!n || n <= 0) { setError('Paydown amount must be > 0'); return; }
    const cap = position.fundedAmount ?? 0;
    if (n > cap) { setError(`Paydown exceeds funded balance ${fmtCcy(cap)}`); return; }
    if (!previewed) { setError('Preview the paydown before confirming'); return; }
    if (!evidenceRef.trim()) { setError('Source / evidence reference is required'); return; }

    setSubmitting(true);
    try {
      // PC only — the LL branch is unreachable (guarded above; backend returns 409).
      await axios.post(`${loanBase(assetKind)}/${position.loanRecordId}/apply-notice`, {
        eventType: 'PAYDOWN',
        amount: n,
        effectiveDate,
        evidenceRef: evidenceRef.trim(),
        idempotencyKey: `pc-loan:${position.loanRecordId}:paydown:${evidenceRef.trim()}`,
      });
      onSuccess();
    } catch (err: any) {
      setError(errMsg(err, 'Failed to record paydown'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="Record Paydown"
      subtitle={subtitleFor(position)}
      icon={<ArrowDownCircle size={18} className="text-emerald-400" />}
      onClose={onClose}
    >
      <Body>
        <LifecycleNotice tone="paydown" title="Operations action — position paydown">
          Reduces funded balance and emits a REPAY trade through the posting engine.
          Idempotent — re-submitting the same amount and date is a no-op.
        </LifecycleNotice>

        <FieldRow>
          <Field>
            <Label>Effective Date</Label>
            <DateField
              value={effectiveDate ?? ''}
              onChange={iso => { setEffectiveDate(iso); resetPreview(); }}
            />
          </Field>
          <Field>
            <Label>Paydown Amount (USD)</Label>
            <Input type="text" inputMode="decimal" value={amount}
              onChange={e => { setAmount(e.target.value); resetPreview(); }} placeholder="e.g. 5MM or 5,000,000" />
            <AmountHint raw={amount} />
          </Field>
        </FieldRow>

        <Field>
          <Label>Source / Evidence Reference</Label>
          <Input type="text" value={evidenceRef}
            onChange={e => { setEvidenceRef(e.target.value); resetPreview(); }}
            placeholder="e.g. agent notice, wire reference, or manual attestation ID" />
        </Field>

        {previewed && (
          <PreviewCard tone="paydown" title={`Preview ready — effective ${effectiveDate} · no IBOR impact until confirmed`}>
            <MetricGrid>
              <Metric label="Funded (current)" value={fmtCcy(position.fundedAmount)} color="#94a3b8" />
              <Metric label="Funded (after paydown)" value={fmtCcy(remaining)} color="#34d399" />
              <Metric label="Commitment (after)" value={fmtCcy(commitmentAfter)} />
              <Metric label="Unfunded (after)" value={fmtCcy(unfundedAfter)} />
              <Metric label="IBOR cash impact" value={`+${fmtCcy(paydownAmount)}`} color="#34d399" />
              <Metric
                label="Facility treatment"
                value={position.reborrowable ? 'Availability restored' : 'Commitment reduced'}
                color="#94a3b8"
              />
            </MetricGrid>
          </PreviewCard>
        )}

        {error && <ErrorBanner>{error}</ErrorBanner>}
      </Body>
      <Footer>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button onClick={runPreview} disabled={submitting || !effectiveDate}>
          Preview
        </Button>
        <Button $primary onClick={submit} disabled={submitting || !previewed || !effectiveDate}>
          {submitting ? 'Submitting…' : 'Confirm Paydown'}
        </Button>
      </Footer>
    </ModalShell>
  );
};

// ============ LL FACILITY-LEVEL DRAWDOWN (governed ops tranche-event) ============
//
// An RCF/DDTL drawdown is a FACILITY-level borrower event (one global draw +
// pro-rata child fundings per holder, FR-9), the funded↑/unfunded↓ inverse of a
// paydown. Posts to the permissioned ops tranche-event endpoint; server-computed
// preview before the operator confirms (FRD §5). Refuses on a fully-funded TLB.

interface DrawdownPreviewHolder {
  portfolioId: number;
  fundedBefore: number;
  unfundedBefore: number;
  appliedAmount: number;
  fundedAfter: number;
  unfundedAfter: number;
  sharePct: number;
  investorStated: boolean;
  defaultingSkipped?: boolean;
}
interface DrawdownPreviewResult {
  ok: boolean;
  message: string;
  drawAmount: number;
  availabilityBefore: number;
  availabilityAfter: number;
  holders: DrawdownPreviewHolder[];
}

const LlFacilityDrawdownModal: React.FC<BaseProps> = ({ position, onClose, onSuccess, initial }) => {
  const [amount, setAmount] = useState(initial?.amount || '');
  const [effectiveDate, setEffectiveDate] = useState(today());
  const [evidenceRef, setEvidenceRef] = useState(initial?.evidenceRef || '');
  const [preview, setPreview] = useState<DrawdownPreviewResult | null>(null);
  const [holderOptions, setHolderOptions] = useState<DrawdownPreviewHolder[]>([]);
  const [useInvestorStated, setUseInvestorStated] = useState(false);
  const [investorStated, setInvestorStated] = useState<Record<number, string>>({});
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetPreview = () => setPreview(null);
  const payload = () => {
    const stated = Object.fromEntries(
      Object.entries(investorStated)
        .map(([portfolioId, raw]) => [portfolioId, parseAmt(raw)])
        .filter(([, value]) => Number(value) > 0),
    );
    return {
      facilityAmount: parseAmt(amount),
      effectiveDate,
      entitlementDate: effectiveDate,
      investorStatedAmounts: useInvestorStated ? stated : null,
      evidenceRef: evidenceRef.trim() || null,
    };
  };

  const runPreview = async () => {
    setError(null);
    const n = parseAmt(amount);
    if (!n || n <= 0) { setError('Draw amount must be > 0'); return; }
    setPreviewing(true);
    try {
      const resp = await axios.post(
        `${LL_TRANCHE_EVENTS_BASE}/tranches/${position.loanRecordId}/drawdown/preview`, payload());
      const result = resp.data as DrawdownPreviewResult;
      setPreview(result);
      if (result.holders?.length) {
        setHolderOptions(result.holders);
        if (!Object.keys(investorStated).length) {
          setInvestorStated(Object.fromEntries(result.holders
            .filter(holder => !holder.defaultingSkipped)
            .map(holder => [holder.portfolioId, String(holder.appliedAmount)])));
        }
      }
    } catch (err: any) {
      setError(errMsg(err, 'Failed to preview drawdown'));
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  };

  const submit = async () => {
    setError(null);
    if (!evidenceRef.trim()) { setError('Agent evidence reference is required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${LL_TRANCHE_EVENTS_BASE}/tranches/${position.loanRecordId}/drawdown`, payload());
      onSuccess();
    } catch (err: any) {
      setError(errMsg(err, 'Failed to record facility drawdown'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="Record Drawdown"
      subtitle={subtitleFor(position)}
      icon={<ArrowUpCircle size={18} className="text-indigo-400" />}
      onClose={onClose}
    >
      <Body>
        <LifecycleNotice tone="drawdown" title="Operations action — facility-level drawdown">
          Records one borrower-level draw and funds settled holders pro-rata against unfunded
          commitment (FRD §5 / FR-9). Requires agent-notice evidence. Only revolving or
          delayed-draw strip facilities can be drawn.
        </LifecycleNotice>

        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8,
          background: 'rgba(15, 23, 42, 0.55)', border: '1px solid rgba(148, 163, 184, 0.18)',
          borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.76rem', color: '#cbd5e1',
        }}>
          <div><span style={{ color: '#94a3b8' }}>Facility type</span><br /><strong>{(position.facilityType || '—').replace(/_/g, ' ')}</strong></div>
          <div><span style={{ color: '#94a3b8' }}>Draw window end</span><br /><strong>{position.drawWindowEnd || position.ddtlExpiry || 'Not applicable'}</strong></div>
          <div title="Global borrower amount across all lenders"><span style={{ color: '#94a3b8' }}>Global outstanding</span><br /><strong>{fmtCcy(position.globalFacilityOutstanding ?? position.ddtlFunded)}</strong></div>
          <div title="Global borrower availability across all lenders"><span style={{ color: '#94a3b8' }}>Global availability</span><br /><strong>{fmtCcy(position.globalFacilityAvailability ?? position.ddtlUndrawn)}</strong></div>
        </div>

        <FieldRow>
          <Field>
            <Label>Effective Date</Label>
            <DateField
              value={effectiveDate ?? ''}
              onChange={iso => { setEffectiveDate(iso); resetPreview(); }}
            />
          </Field>
          <Field>
            <Label>Draw Amount (USD)</Label>
            <Input type="text" inputMode="decimal" value={amount}
              onChange={e => { setAmount(e.target.value); resetPreview(); }} placeholder="e.g. 15MM or 15,000,000" />
            <AmountHint raw={amount} />
          </Field>
        </FieldRow>

        <FutureEffectiveNote effectiveDate={effectiveDate} verb="draw" />

        <Field>
          <Label>Agent Evidence Ref</Label>
          <Input type="text" value={evidenceRef}
            onChange={e => setEvidenceRef(e.target.value)} placeholder="e.g. BofA-DRAW-HTPK-20260720" />
        </Field>

        {holderOptions.length > 0 && (
          <div style={{
            border: '1px solid rgba(148, 163, 184, 0.18)', borderRadius: 8,
            padding: '0.75rem 1rem', background: 'rgba(15, 23, 42, 0.45)',
          }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#e2e8f0', fontSize: '0.8rem', fontWeight: 600 }}>
              <input
                type="checkbox"
                checked={useInvestorStated}
                onChange={event => { setUseInvestorStated(event.target.checked); resetPreview(); }}
              />
              Use investor-stated holder allocations
            </label>
            <div style={{ color: '#94a3b8', fontSize: '0.72rem', marginTop: 4 }}>
              Leave off for governed pro-rata allocation. When enabled, each amount is applied directly and revalidated by the backend.
            </div>
            {useInvestorStated && holderOptions.map(holder => (
              <div key={holder.portfolioId} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, alignItems: 'center', marginTop: 8 }}>
                <div style={{ color: '#cbd5e1', fontSize: '0.76rem' }}>
                  Portfolio {holder.portfolioId}<br />
                  <span style={{ color: '#94a3b8' }}>Unfunded capacity {fmtCcy(holder.unfundedBefore)}</span>
                </div>
                <Input
                  type="text"
                  inputMode="decimal"
                  aria-label={`Portfolio ${holder.portfolioId} stated draw amount`}
                  value={investorStated[holder.portfolioId] || ''}
                  onChange={event => {
                    setInvestorStated(current => ({ ...current, [holder.portfolioId]: event.target.value }));
                    resetPreview();
                  }}
                  placeholder="Holder amount"
                />
              </div>
            ))}
          </div>
        )}

        {preview && (
          <PreviewCard tone="drawdown" ok={preview.ok} title={preview.message}>
            <div style={{ color: '#cbd5e1', marginBottom: preview.holders.length ? 8 : 0 }}>
              Availability {fmtCcy(preview.availabilityBefore)} →{' '}
              <strong style={{ color: '#e2e8f0' }}>{fmtCcy(preview.availabilityAfter)}</strong>
            </div>
            {preview.holders.map(h => (
              <div key={h.portfolioId} style={{ color: '#cbd5e1', padding: '2px 0' }}>
                Portfolio target {h.portfolioId}{h.investorStated ? ' (investor-stated)' : ''}{h.defaultingSkipped ? ' (defaulting lender skipped)' : ''}: funded{' '}
                {fmtCcy(h.fundedBefore)} → <strong style={{ color: '#e2e8f0' }}>{fmtCcy(h.fundedAfter)}</strong>,
                {' '}unfunded {fmtCcy(h.unfundedBefore)} → {fmtCcy(h.unfundedAfter)} (+{fmtCcy(h.appliedAmount)})
              </div>
            ))}
          </PreviewCard>
        )}

        {error && <ErrorBanner>{error}</ErrorBanner>}
      </Body>
      <Footer>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button onClick={runPreview} disabled={previewing || submitting || !effectiveDate}>
          {previewing ? 'Previewing…' : 'Preview'}
        </Button>
        <Button $primary onClick={submit} disabled={submitting || !preview?.ok || !effectiveDate}>
          {submitting ? 'Applying…' : 'Confirm Drawdown'}
        </Button>
      </Footer>
    </ModalShell>
  );
};

// ============ DRAWDOWN ============

export const PcDrawdownModal: React.FC<BaseProps> = ({ position, assetKind, onClose, onSuccess, initial }) => {
  const [amount, setAmount] = useState(initial?.amount || '');
  const [effectiveDate, setEffectiveDate] = useState(today());
  const [evidenceRef, setEvidenceRef] = useState(initial?.evidenceRef || '');
  const [previewed, setPreviewed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fundedAfter = useMemo(() => (position.fundedAmount ?? 0) + (parseAmt(amount) || 0), [amount, position]);
  const unfundedAfter = useMemo(
    () => Math.max(0, (position.unfundedAmount ?? 0) - (parseAmt(amount) || 0)), [amount, position]);

  // DDTL (delayed-draw) — availability is tracked on the tranche columns and a
  // draw is gated by the draw-window (ddtlExpiry), not the loan's unfunded
  // commitment. When the loan is a DDTL we surface the tranche meter, cap the
  // draw at ddtlUndrawn, block post-expiry dates, and route to /ddtl-draw
  // (which re-enforces both guards authoritatively).
  const isDdtl = position.facilityType === 'DELAYED_DRAW';
  const ddtlUndrawn = position.ddtlUndrawn ?? 0;
  const ddtlCommitment = position.ddtlCommitment ?? ((position.ddtlFunded ?? 0) + ddtlUndrawn);
  const ddtlFunded = position.ddtlFunded ?? Math.max(0, ddtlCommitment - ddtlUndrawn);
  const daysToExpiry = useMemo(() => {
    if (!position.ddtlExpiry) return null;
    const ms = new Date(position.ddtlExpiry).getTime() - new Date(today()).getTime();
    return Math.round(ms / 86_400_000);
  }, [position.ddtlExpiry]);
  const windowClosed = isDdtl && position.ddtlExpiry != null && effectiveDate > position.ddtlExpiry;

  if (assetKind === 'LL') {
    // LL drawdown is a facility-level ops tranche-event
    // (RCF/DDTL strips), not a portfolio-scoped modal. Render the permissioned
    // facility drawdown ticket (preview + apply via /api/credit/ll/tranche-events).
    return <LlFacilityDrawdownModal position={position} assetKind={assetKind}
      onClose={onClose} onSuccess={onSuccess} />;
  }

  const resetPreview = () => setPreviewed(false);

  const validateDraw = (): number | null => {
    const n = parseAmt(amount);
    if (!n || n <= 0) { setError('Draw amount must be > 0'); return null; }
    if (isDdtl) {
      if (n > ddtlUndrawn) {
        setError(`Draw exceeds DDTL undrawn availability ${fmtCcy(ddtlUndrawn)}`); return null;
      }
      if (windowClosed) {
        setError(`DDTL draw window closed on ${position.ddtlExpiry}`); return null;
      }
    } else if (position.unfundedAmount != null && n > position.unfundedAmount) {
      setError(`Draw exceeds unfunded commitment ${fmtCcy(position.unfundedAmount)}`); return null;
    }
    return n;
  };

  const runPreview = () => {
    setError(null);
    const n = validateDraw();
    if (n == null) return;
    setPreviewed(true);
  };

  const submit = async () => {
    setError(null);
    const n = validateDraw();
    if (n == null) return;
    if (!previewed) { setError('Preview the drawdown before confirming'); return; }
    if (!evidenceRef.trim()) { setError('Source / evidence reference is required'); return; }
    setSubmitting(true);
    try {
      // PC only — the LL branch is unreachable (guarded above; backend returns 409).
      if (isDdtl) {
        await axios.post(`${loanBase(assetKind)}/${position.loanRecordId}/ddtl-draw`, {
          amount: n,
          effectiveDate,
          portfolioId: position.portfolioId,
          evidenceRef: evidenceRef.trim(),
          idempotencyKey: `pc-loan:${position.loanRecordId}:ddtl-draw:${evidenceRef.trim()}`,
        });
      } else {
        await axios.post(`${loanBase(assetKind)}/${position.loanRecordId}/apply-notice`, {
          eventType: 'DRAWDOWN',
          amount: n,
          effectiveDate,
          evidenceRef: evidenceRef.trim(),
          idempotencyKey: `pc-loan:${position.loanRecordId}:drawdown:${evidenceRef.trim()}`,
        });
      }
      onSuccess();
    } catch (err: any) {
      setError(errMsg(err, 'Failed to fund drawdown'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="Record Drawdown"
      subtitle={subtitleFor(position)}
      icon={<ArrowUpCircle size={18} className={isDdtl ? 'text-amber-400' : 'text-indigo-400'} />}
      onClose={onClose}
    >
      <Body>
        <LifecycleNotice tone="drawdown" title={isDdtl ? 'Operations action — DDTL drawdown' : 'Operations action — position drawdown'}>
          {isDdtl
            ? 'Funds a delayed-draw tranche from remaining availability and updates funded and unfunded exposure through IBOR.'
            : 'Funds unfunded commitment and emits a drawdown event through the posting engine.'}
        </LifecycleNotice>

        <FieldRow>
          <Field>
            <Label>Effective Date</Label>
            <DateField
              value={effectiveDate ?? ''}
              max={isDdtl ? position.ddtlExpiry : undefined}
              onChange={iso => { setEffectiveDate(iso); resetPreview(); }}
            />
          </Field>
          <Field>
            <Label>Draw Amount (USD)</Label>
            <Input type="text" inputMode="decimal" value={amount}
              onChange={e => { setAmount(e.target.value); resetPreview(); }} placeholder="e.g. 2.5MM or 2,500,000" />
            <AmountHint raw={amount} />
          </Field>
        </FieldRow>

        <Field>
          <Label>Source / Evidence Reference</Label>
          <Input type="text" value={evidenceRef}
            onChange={e => { setEvidenceRef(e.target.value); resetPreview(); }}
            placeholder="e.g. borrower draw request or manual attestation ID" />
        </Field>

        {isDdtl && <DdtlAvailabilityMeter
          commitment={ddtlCommitment} funded={ddtlFunded} undrawn={ddtlUndrawn}
          drawAmount={parseAmt(amount) || 0} expiry={position.ddtlExpiry} daysToExpiry={daysToExpiry} />}

        {previewed && (
          <PreviewCard tone="drawdown" title={`Preview ready — effective ${effectiveDate} · no IBOR impact until confirmed`}>
            <MetricGrid columns={3}>
              <Metric label="Funded (current)" value={fmtCcy(position.fundedAmount)} color="#94a3b8" />
              <Metric label="Funded (after draw)" value={fmtCcy(fundedAfter)} color="#34d399" />
              <Metric label="Unfunded (after draw)" value={fmtCcy(unfundedAfter)} color="#818cf8" />
            </MetricGrid>
          </PreviewCard>
        )}

        {isDdtl && windowClosed && (
          <ErrorBanner>DDTL draw window closed on {position.ddtlExpiry} — pick an effective date on or before expiry.</ErrorBanner>
        )}
        {error && <ErrorBanner>{error}</ErrorBanner>}
      </Body>
      <Footer>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button onClick={runPreview} disabled={submitting || windowClosed || !effectiveDate}>
          Preview
        </Button>
        <Button $primary onClick={submit} disabled={submitting || windowClosed || !previewed || !effectiveDate}>
          {submitting ? 'Applying…' : 'Confirm Drawdown'}
        </Button>
      </Footer>
    </ModalShell>
  );
};

// ============ RATE RESET ============

export const PcRateResetModal: React.FC<BaseProps> = ({ position, assetKind, onClose, onSuccess }) => {
  // R4-7: the two asset classes have DIFFERENT rate semantics.
  //   LL: the user enters the INDEX FIXING in percent (e.g. SOFR 5.25); the
  //       backend derives all-in = max(index + CSA, floor) + spread and writes
  //       an effective-dated ll_tranche_terms row (the timeline daily accrual
  //       reads). Sent as `indexFixingPct` — the old `baseRate` field is a
  //       DECIMAL-fraction contract (0.0525) and sending a percent through it
  //       produced a ~100x-inflated index.
  //   PC: the user enters/overrides governed formula components. The server
  //       independently derives and persists all-in; it never accepts a
  //       client-supplied all-in coupon.
  const isLl = assetKind === 'LL';
  const impliedIndex = position.currentIndexFixingPct
    ?? Math.max(0, (position.allInRate ?? 0)
      - (position.spreadBps ?? 0) / 100
      - (position.csaBps ?? 0) / 100);
  const [rateInput, setRateInput] = useState(
    Number.isFinite(impliedIndex) && impliedIndex > 0 ? String(impliedIndex) : '');
  const [spreadBps, setSpreadBps] = useState(String(position.spreadBps ?? ''));
  const [floorRatePct, setFloorRatePct] = useState(String(position.floorRate ?? 0));
  const [csaBps, setCsaBps] = useState(String(position.csaBps ?? 0));
  const [effectiveDate, setEffectiveDate] = useState(today());
  const [evidenceRef, setEvidenceRef] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // F-PCP-053: like every other servicing form, the reset is previewed before it is applied; any change to an
  // input invalidates the preview so Confirm never applies figures the operator has not seen.
  const [previewed, setPreviewed] = useState(false);
  useEffect(() => { setPreviewed(false); }, [rateInput, spreadBps, floorRatePct, csaBps, effectiveDate, evidenceRef]);
  const resetIndex = Number(rateInput) || 0;
  const resetSpread = Number(spreadBps) || 0;
  const resetFloor = Number(floorRatePct) || 0;
  const resetCsa = Number(csaBps) || 0;
  const resetAllIn = Math.max(resetIndex + resetCsa / 100, resetFloor) + resetSpread / 100;

  const submit = async () => {
    setError(null);
    const n = Number(rateInput);
    if (!rateInput || n <= 0) {
      setError('Index fixing is required');
      return;
    }
    const spread = Number(spreadBps);
    const floor = Number(floorRatePct);
    const csa = Number(csaBps);
    if (!isLl && (!Number.isFinite(spread) || spread < 0
      || !Number.isFinite(floor) || floor < 0
      || !Number.isFinite(csa) || csa < 0)) {
      setError('Spread, floor, and CSA must be non-negative numbers');
      return;
    }
    if (!evidenceRef.trim()) { setError('Source / evidence reference is required'); return; }
    setSubmitting(true);
    try {
      if (isLl) {
        // Percent fixing -> effective-dated terms row + header/SMF coupon sync.
        // Future-dated fixings are refused server-side with 409.
        await axios.post(`${loanBase(assetKind)}/${position.loanRecordId}/rate-reset`, {
          indexFixingPct: n, effectiveDate, evidenceRef: evidenceRef.trim(),
        });
      } else {
        await axios.post(`${loanBase(assetKind)}/${position.loanRecordId}/apply-notice`, {
          eventType: 'RATE_RESET',
          indexFixingPct: n,
          spreadBps: spread,
          floorRatePct: floor,
          csaBps: csa,
          effectiveDate,
          evidenceRef: evidenceRef.trim(),
          idempotencyKey: `pc-loan:${position.loanRecordId}:rate-reset:${evidenceRef.trim()}`,
        });
      }
      onSuccess();
    } catch (err: any) {
      setError(errMsg(err, 'Failed to apply rate reset'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="Rate Reset"
      subtitle={subtitleFor(position)}
      icon={<RefreshCw size={18} className="text-blue-400" />}
      onClose={onClose}
    >
      <Body>
        <LifecycleNotice tone="rate" title="Operations action — floating-rate reset">
          Enter the new index fixing in percent. The all-in rate is derived, not entered:
          all-in = max(index + CSA, floor) + spread.
        </LifecycleNotice>

        <FieldRow>
          <Field>
            <Label>Effective Date</Label>
            <DateField value={effectiveDate ?? ''} onChange={iso => setEffectiveDate(iso)} />
          </Field>
          <Field>
            <Label>Current {position.baseRate || 'SOFR'} Fixing (%)</Label>
            <Input type="number" step="0.0001" value={rateInput}
              onChange={e => setRateInput(e.target.value)}
              placeholder="e.g. 5.25" />
          </Field>
        </FieldRow>

        <FieldRow>
          <Field>
            <Label>Spread (bps)</Label>
            <Input type="number" step="1" value={spreadBps}
              onChange={e => setSpreadBps(e.target.value)}
              disabled={isLl}
              title={isLl ? 'LL spread is governed by loan terms; reset only updates the index fixing.' : undefined} />
          </Field>
          <Field>
            <Label>Floor (%)</Label>
            <Input type="number" step="0.0001" value={floorRatePct}
              onChange={e => setFloorRatePct(e.target.value)}
              disabled={isLl}
              title={isLl ? 'LL floor is governed by loan terms; reset only updates the index fixing.' : undefined} />
          </Field>
          <Field>
            <Label>CSA (bps)</Label>
            <Input type="number" step="1" value={csaBps}
              onChange={e => setCsaBps(e.target.value)}
              disabled={isLl}
              title={isLl ? 'LL CSA is governed by loan terms; reset only updates the index fixing.' : undefined} />
          </Field>
        </FieldRow>

        <Field>
          <Label>Source / Evidence Reference</Label>
          <Input type="text" value={evidenceRef}
            onChange={e => setEvidenceRef(e.target.value)}
            placeholder="e.g. rate-set notice, screen capture reference, or attestation ID" />
        </Field>

        {previewed && (
          <PreviewCard tone="rate" title={`Rate reset effective ${effectiveDate}: all-in ${resetAllIn.toFixed(4)}%`}>
            max({position.baseRate || 'SOFR'} {resetIndex.toFixed(4)}% + CSA {resetCsa.toFixed(2)} bps,
            {' '}floor {resetFloor.toFixed(4)}%) + spread {resetSpread.toFixed(2)} bps.
            {' '}Books a rate-reset ticket on the facility and a new pricing terms row; evidence: {evidenceRef.trim() || 'none given'}.
          </PreviewCard>
        )}

        <MetricGrid>
          <Metric
            label="Current All-In"
            value={position.allInRate != null ? `${position.allInRate.toFixed(2)}%` : 'N/A'}
            color="#94a3b8"
          />
          <Metric
            label="Current Index + Spread"
            value={`${position.baseRate || '—'} + ${position.spreadBps ?? '—'} bps`}
            color="#94a3b8"
          />
        </MetricGrid>

        {error && <ErrorBanner>{error}</ErrorBanner>}
      </Body>
      <Footer>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button onClick={() => setPreviewed(true)} disabled={submitting || !effectiveDate || !(Number(rateInput) > 0) || !evidenceRef.trim()}>Preview</Button>
        <Button $primary onClick={submit} disabled={submitting || !effectiveDate || !previewed}>
          {submitting ? 'Applying…' : 'Confirm Reset'}
        </Button>
      </Footer>
    </ModalShell>
  );
};

// ============ AMEND ============

interface CovUpdateRow { covenantName: string; newThresholdValue: string; }

const CovenantUpdateRowCard: React.FC<{
  row: CovUpdateRow;
  index: number;
  canRemove: boolean;
  availableCovenants: { name: string; threshold: string }[];
  onChange: (patch: Partial<CovUpdateRow>) => void;
  onRemove: () => void;
}> = ({ row, index, canRemove, availableCovenants, onChange, onRemove }) => (
  <div style={{
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: 8,
    padding: '0.75rem',
    display: 'grid',
    gap: 10,
    background: 'rgba(15, 23, 42, 0.45)',
  }}>
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 8, alignItems: 'end' }}>
      <Field>
        <Label>Covenant</Label>
        {availableCovenants.length > 0 ? (
          <Select value={row.covenantName} onChange={e => onChange({ covenantName: e.target.value })}>
            <option value="">Select covenant…</option>
            {availableCovenants.map(c => (
              <option key={c.name} value={c.name}>{c.name}{c.threshold ? ` (now ${c.threshold})` : ''}</option>
            ))}
          </Select>
        ) : (
          <Input type="text" value={row.covenantName}
            onChange={e => onChange({ covenantName: e.target.value })}
            placeholder="e.g. Total Net Leverage" />
        )}
      </Field>
      <Button onClick={onRemove} disabled={!canRemove}
        title="Remove covenant change" style={{ padding: '0 0.6rem', height: 40 }}><Trash2 size={14} /></Button>
    </div>
    <Field>
      <Label>New Threshold</Label>
      <Input type="text" inputMode="decimal" value={row.newThresholdValue}
        onChange={e => onChange({ newThresholdValue: e.target.value })}
        placeholder="new threshold" />
    </Field>
    <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
      Change {index + 1}: the proposed threshold is included in this amendment.
    </div>
  </div>
);

export const PcAmendModal: React.FC<BaseProps> = ({ position, assetKind, onClose, onSuccess }) => {
  const [amendmentName, setAmendmentName] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(today());
  const [evidenceRef, setEvidenceRef] = useState('');
  const [newSpreadBps, setNewSpreadBps] = useState('');
  const [newCommitment, setNewCommitment] = useState('');
  const [amendmentFee, setAmendmentFee] = useState('');
  const [notes, setNotes] = useState('');
  const [rows, setRows] = useState<CovUpdateRow[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load the loan's covenants so the threshold change is a pick-list, not a free-text
  // name that has to match a stored covenant exactly (a silent no-op if it doesn't).
  const [availableCovenants, setAvailableCovenants] = useState<{ name: string; threshold: string }[]>([]);
  useEffect(() => {
    if (!position?.securityId) return;
    axios.get(covenantPath(assetKind, position.securityId))
      .then(res => {
        const covs = Array.isArray(res.data) ? res.data : [];
        setAvailableCovenants(covs.map((c: any) => ({
          name: c.covenantName || '',
          threshold: c.thresholdValue != null ? String(c.thresholdValue) : '',
        })));
      })
      .catch(() => setAvailableCovenants([]));
  }, [assetKind, position?.securityId]);

  const setRow = (i: number, patch: Partial<CovUpdateRow>) =>
    setRows(rs => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows(rs => [...rs, { covenantName: '', newThresholdValue: '' }]);
  const removeRow = (i: number) => setRows(rs => rs.filter((_, idx) => idx !== i));
  const amendmentSpread = newSpreadBps ? Number(newSpreadBps) : (position.spreadBps ?? 0);
  const amendmentIndex = position.currentIndexFixingPct ?? 0;
  const amendmentFloor = position.floorRate ?? 0;
  const amendmentCsa = position.csaBps ?? 0;
  const amendmentAllIn = Math.max(amendmentIndex + amendmentCsa / 100, amendmentFloor)
    + amendmentSpread / 100;
  const pricingAvailable = position.currentIndexFixingPct != null && position.floorRate != null
    && position.csaBps != null && (newSpreadBps !== '' || position.spreadBps != null);

  const submit = async () => {
    setError(null);
    if (!effectiveDate) { setError('Enter an effective date'); return; }
    if (newSpreadBps && !Number.isFinite(Number(newSpreadBps))) { setError('Enter a valid spread'); return; }
    if (rows.some(r => !r.covenantName.trim() || !r.newThresholdValue.trim() || !Number.isFinite(Number(r.newThresholdValue)))) {
      setError('Complete each covenant change or remove the unused row'); return;
    }
    const covenantUpdates = rows
      .filter(r => r.covenantName.trim() && r.newThresholdValue !== '')
      .map(r => ({ covenantName: r.covenantName.trim(), newThresholdValue: Number(r.newThresholdValue) }));
    if (covenantUpdates.length === 0 && !newSpreadBps && !newCommitment) {
      setError('Add at least one covenant, spread, or facility-capacity change'); return;
    }
    if (newCommitment && parseAmt(newCommitment) <= 0) {
      setError('New facility capacity must be greater than zero'); return;
    }
    if (!evidenceRef.trim()) { setError('Source / evidence reference is required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${loanBase(assetKind)}/${position.loanRecordId}/amend`, {
        amendmentName: amendmentName.trim() || 'Manual Amendment',
        effectiveDate,
        covenantUpdates,
        newSpreadBps: newSpreadBps ? Number(newSpreadBps) : null,
        ...(assetKind === 'LL' && newCommitment
          ? { newCommitment: parseAmt(newCommitment) }
          : {}),
        newAllInRate: null, // all-in is recomputed server-side from the new spread
        ...(assetKind === 'PC' && amendmentFee
          ? { amendmentFee: parseAmt(amendmentFee) }
          : {}),
        evidenceRef: evidenceRef.trim(),
        notes: [`Evidence: ${evidenceRef.trim()}`, notes.trim()].filter(Boolean).join('; '),
      });
      onSuccess();
    } catch (err: any) {
      setError(errMsg(err, 'Failed to apply amendment'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LoanAmendmentWorkspace subtitle={subtitleFor(position)} assetKind={assetKind} busy={submitting} error={error} onClose={onClose}
      actions={<><Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button $primary onClick={submit} disabled={submitting || !effectiveDate}>
          {submitting ? 'Submitting…' : assetKind === 'PC' ? 'Submit for Approval' : 'Apply Amendment'}
        </Button></>}
      summary={<>
        <div><h3>Review proposed changes</h3><p>Compare this proposal with the loan values currently available.</p>
          <dl>
            <dt>Spread · current → proposed</dt>
            <dd>{position.spreadBps != null ? position.spreadBps + ' bps' : 'Not recorded'} → {newSpreadBps ? newSpreadBps + ' bps' : 'Unchanged'}</dd>
            <dt>All-in rate · current → indicative</dt>
            <dd>{position.allInRate != null ? position.allInRate.toFixed(4) + '%' : 'Not recorded'} → {newSpreadBps
              ? pricingAvailable ? amendmentAllIn.toFixed(4) + '%' : 'Pricing inputs unavailable'
              : 'Unchanged'}</dd>
            {assetKind === 'LL' && newCommitment && <><dt>Proposed facility capacity</dt><dd>{fmtCcy(parseAmt(newCommitment))}</dd></>}
            {assetKind === 'PC' && amendmentFee && <><dt>Amendment fee</dt><dd>{fmtCcy(parseAmt(amendmentFee))}</dd></>}
            {rows.filter(r => r.covenantName && r.newThresholdValue).map((row, index) => <React.Fragment key={index}>
              <dt>{row.covenantName} · current → proposed</dt><dd>{availableCovenants.find(c => c.name === row.covenantName)?.threshold || 'Not recorded'} → {row.newThresholdValue}</dd>
            </React.Fragment>)}
            <dt>Effective date</dt><dd>{effectiveDate || 'Required'}</dd>
          </dl>
        </div>
        <div><h3>{assetKind === 'PC' ? 'Independent approval' : 'Terms update'}</h3>
          <p>{assetKind === 'PC'
            ? 'Submitting creates a proposal. A different authorized reviewer must approve it before loan terms change.'
            : 'Apply Amendment sends these changes to the loan terms workflow. Review the effective date and evidence before applying.'}</p>
        </div>
        <div><h3>Evidence reference</h3><p style={{ overflowWrap: 'anywhere' }}>{evidenceRef.trim() || 'Add the agreement, consent or notice reference supporting these changes.'}</p></div>
      </>}>
      <section aria-label="Amendment details">
        <div><h3>Amendment details</h3><p>Identify the change and when it takes effect.</p></div>
        <FieldRow>
          <Field><Label htmlFor="loan-amend-name">Amendment name</Label><Input id="loan-amend-name" value={amendmentName}
            onChange={e => setAmendmentName(e.target.value)} placeholder="e.g. Amendment No. 3" /></Field>
          <Field><Label htmlFor="loan-amend-date">Effective date</Label><DateField
            id="loan-amend-date"
            value={effectiveDate ?? ''}
            onChange={iso => setEffectiveDate(iso)}
          /></Field>
        </FieldRow>
        <Field><Label htmlFor="loan-amend-evidence">Source / evidence reference · required</Label>
          <Input id="loan-amend-evidence" value={evidenceRef} onChange={e => setEvidenceRef(e.target.value)}
            placeholder="Agreement, consent or notice reference" /></Field>
      </section>
      <section aria-label="Pricing and economics">
        <div><h3>Pricing & economics</h3><p>Enter only the terms being amended. Blank fields retain their current values.</p></div>
        <FieldRow>
          <Field><Label htmlFor="loan-amend-spread">New spread (bps)</Label>
            <Input id="loan-amend-spread" type="number" step="1" value={newSpreadBps} onChange={e => setNewSpreadBps(e.target.value)}
              placeholder={position.spreadBps != null ? 'Current: ' + position.spreadBps : 'Enter proposed spread'} /></Field>
          {assetKind === 'PC'
            ? <Field><Label htmlFor="loan-amend-fee">Amendment fee (USD)</Label><Input id="loan-amend-fee" value={amendmentFee}
                onChange={e => setAmendmentFee(e.target.value)} placeholder="e.g. 187,500" /><AmountHint raw={amendmentFee} /></Field>
            : <Field><Label htmlFor="loan-amend-capacity">New facility capacity (USD)</Label><Input id="loan-amend-capacity" value={newCommitment}
                onChange={e => setNewCommitment(e.target.value)} placeholder="e.g. 125MM" /><AmountHint raw={newCommitment} /></Field>}
        </FieldRow>
        {newSpreadBps && <p>{pricingAvailable
          ? 'Indicative rate uses the current index fixing (' + amendmentIndex.toFixed(4) + '%), CSA (' + amendmentCsa.toFixed(2) + ' bps) and floor (' + amendmentFloor.toFixed(4) + '%). Final pricing is calculated when the amendment is processed.'
          : 'An indicative all-in rate is unavailable because some current pricing components are not recorded.'}</p>}
      </section>
      <section aria-label="Covenant changes">
        <div><h3>Covenant changes</h3><p>{rows.length ? 'Review the current threshold beside each proposed change.' : 'No covenant changes added. Existing thresholds will be retained.'}</p></div>
        {rows.map((r, i) => <CovenantUpdateRowCard key={i} row={r} index={i} canRemove
          availableCovenants={availableCovenants} onChange={patch => setRow(i, patch)} onRemove={() => removeRow(i)} />)}
        <Button onClick={addRow} style={{ justifySelf: 'start', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Plus size={14} /> Add covenant change
        </Button>
      </section>
      <section><h3>Rationale & notes</h3><Field><Label htmlFor="loan-amend-notes">Supporting context · optional</Label>
        <Input id="loan-amend-notes" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Explain the reason for these changes" /></Field></section>
    </LoanAmendmentWorkspace>
  );
};

// ============ WRITEDOWN ============

export const PcWritedownModal: React.FC<BaseProps> = ({ position, assetKind, onClose, onSuccess }) => {
  const [newCarryValue, setNewCarryValue] = useState('');
  const [newStatus, setNewStatus] = useState('WATCHLIST');
  const [effectiveDate, setEffectiveDate] = useState(today());
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const writedownAmt = useMemo(() => {
    const cur = position.fundedAmount ?? 0;
    return Math.max(0, cur - (parseAmt(newCarryValue) || 0));
  }, [newCarryValue, position]);

  const submit = async () => {
    setError(null);
    if (!newCarryValue) { setError('New carry value is required'); return; }
    const newCV = parseAmt(newCarryValue);
    if (newCV < 0) { setError('Carry value cannot be negative'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${loanBase(assetKind)}/${position.loanRecordId}/writedown`, {
        newCarryValue: newCV,
        newStatus,
        effectiveDate,
        idempotencyKey: `pc-loan:${position.loanRecordId}:writedown:${effectiveDate}:${newCV}`,
        notes: notes || null,
      });
      onSuccess();
    } catch (err: any) {
      setError(errMsg(err, 'Failed to record writedown'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="Impairment / Writedown"
      subtitle={subtitleFor(position)}
      icon={<AlertTriangle size={18} className="text-amber-400" />}
      onClose={onClose}
    >
      <Body>
        <LifecycleNotice tone="warning" title="Operations action — impairment / writedown">
          Marks the loan to a lower carry value and updates status. Emits an MTM/IMPAIRMENT
          event through the posting engine. Recovery requires a separate event.
        </LifecycleNotice>

        <FieldRow>
          <Field>
            <Label>Effective Date</Label>
            <DateField value={effectiveDate ?? ''} onChange={iso => setEffectiveDate(iso)} />
          </Field>
          <Field>
            <Label>New Carry Value (USD)</Label>
            <Input type="text" inputMode="decimal" value={newCarryValue}
              onChange={e => setNewCarryValue(e.target.value)} placeholder="e.g. 42MM or 42,000,000" />
            <AmountHint raw={newCarryValue} />
          </Field>
        </FieldRow>

        <Field>
          <Label>New Status</Label>
          <Select value={newStatus} onChange={e => setNewStatus(e.target.value)}>
            <option value="WATCHLIST">Watchlist</option>
            <option value="DEFAULTED">Defaulted</option>
            <option value="REFINANCED">Refinanced</option>
          </Select>
        </Field>

        <Field>
          <Label>Notes / Justification</Label>
          <Input type="text" value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="e.g. Q1 2026 borrower EBITDA down 30%, covenant breach + missed amortization" />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <Field>
            <Label>Funded (current)</Label>
            <div style={{ color: '#94a3b8', fontWeight: 600 }}>{fmtCcy(position.fundedAmount)}</div>
          </Field>
          <Field>
            <Label>Writedown Amount</Label>
            <div style={{ color: writedownAmt > 0 ? '#fbbf24' : '#94a3b8', fontWeight: 700 }}>
              {fmtCcy(writedownAmt)}
            </div>
          </Field>
        </div>

        {error && <ErrorBanner>{error}</ErrorBanner>}
      </Body>
      <Footer>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button $danger onClick={submit} disabled={submitting || !effectiveDate}>
          {submitting ? 'Submitting…' : 'Record Writedown'}
        </Button>
      </Footer>
    </ModalShell>
  );
};

// ============ PAYOFF ============

export const PcPayoffModal: React.FC<BaseProps> = ({ position, assetKind, onClose, onSuccess }) => {
  const [effectiveDate, setEffectiveDate] = useState(today());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      // PC closes via /mature; LL via /payoff (both repay the balance + close the position).
      const path = assetKind === 'LL' ? 'payoff' : 'mature';
      await axios.post(`${loanBase(assetKind)}/${position.loanRecordId}/${path}`, { effectiveDate });
      onSuccess();
    } catch (err: any) {
      setError(errMsg(err, 'Failed to record payoff'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="Full Payoff"
      subtitle={subtitleFor(position)}
      icon={<Banknote size={18} className="text-emerald-400" />}
      onClose={onClose}
    >
      <Body>
        <LifecycleNotice tone="paydown" title="Operations action — full payoff">
          Repays the full funded balance, marks the loan MATURED, and closes the IBOR
          position at zero via a REPAY trade.
        </LifecycleNotice>

        <FieldRow>
          <Field>
            <Label>Effective Date</Label>
            <DateField value={effectiveDate ?? ''} onChange={iso => setEffectiveDate(iso)} />
          </Field>
          <Field>
            <Label>Payoff Amount</Label>
            <div style={{ color: '#34d399', fontSize: '1rem', fontWeight: 700, paddingTop: 6 }}>
              {fmtCcy(position.fundedAmount)}
            </div>
          </Field>
        </FieldRow>

        {error && <ErrorBanner>{error}</ErrorBanner>}
      </Body>
      <Footer>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button $primary onClick={submit} disabled={submitting || !effectiveDate}>
          {submitting ? 'Submitting…' : 'Confirm Payoff'}
        </Button>
      </Footer>
    </ModalShell>
  );
};

// ============ RECORD COVENANT TEST ============

interface CovTestRow {
  covenantName: string;
  numerator: string;
  denominator: string;
  threshold: string;
  operator: string;
}

export const PcCovenantTestModal: React.FC<BaseProps> = ({ position, assetKind, onClose, onSuccess }) => {
  const [periodEndDate, setPeriodEndDate] = useState(today());
  const [evidenceRef, setEvidenceRef] = useState('');
  const [rows, setRows] = useState<CovTestRow[]>([
    { covenantName: '', numerator: '', denominator: '', threshold: '', operator: '<=' },
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-populate the loan's own covenants (name, threshold, direction) so the user
  // only fills in the actual value — no re-keying names that must match a stored
  // covenant exactly. Falls back to the blank row if the loan has none / fetch fails.
  useEffect(() => {
    if (!position?.securityId) return;
    axios.get(covenantPath(assetKind, position.securityId))
      .then(res => {
        const covs = Array.isArray(res.data) ? res.data : [];
        if (covs.length) {
          setRows(covs.map((c: any) => ({
            covenantName: c.covenantName || '',
            numerator: '',
            denominator: '',
            threshold: c.thresholdValue != null ? String(c.thresholdValue) : '',
            operator: c.thresholdType === 'MAX' ? '<=' : '>=',
          })));
        }
      })
      .catch(() => { /* keep the default blank row */ });
  }, [assetKind, position?.securityId]);

  const setRow = (i: number, patch: Partial<CovTestRow>) =>
    setRows(rs => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows(rs => [...rs, {
    covenantName: '', numerator: '', denominator: '', threshold: '', operator: '<=',
  }]);
  const removeRow = (i: number) => setRows(rs => (rs.length > 1 ? rs.filter((_, idx) => idx !== i) : rs));

  const submit = async () => {
    setError(null);
    if (!position.borrowerName) { setError('Borrower could not be resolved for this position'); return; }
    // Money inputs take the platform shorthand (25MM, 500K); ratios and plain numbers pass through (F-PAR-021).
    const amount = (raw: string): number => (/[KMB]$/i.test(raw.trim())
      ? parseAmountShorthand(raw) : Number(raw.replace(/[,$\s]/g, '')));
    const unreadable = rows.find(r => r.covenantName.trim()
      && [r.numerator, r.denominator, r.threshold].some(v => v.trim() && Number.isNaN(amount(v))));
    if (unreadable) {
      setError(`${unreadable.covenantName}: enter numbers such as 4.24, 31,200,000 or 31.2MM`);
      return;
    }
    const zeroDenominator = rows.find(r => r.denominator.trim() && amount(r.denominator) === 0);
    if (zeroDenominator) {
      setError(`${zeroDenominator.covenantName || 'Covenant'}: denominator cannot be zero`);
      return;
    }
    const covenants = rows
      .filter(r => r.covenantName.trim() && r.numerator !== '' && r.threshold !== '')
      .map(r => {
        const numerator = amount(r.numerator);
        const denominator = r.denominator.trim() ? amount(r.denominator) : null;
        const actual = denominator == null ? numerator : numerator / denominator;
        const threshold = amount(r.threshold);
        const passed = r.operator === '<=' ? actual <= threshold : actual >= threshold;
        return {
          covenantName: r.covenantName.trim(),
          numerator,
          denominator,
          calculatedRatio: actual,
          threshold,
          operator: r.operator,
          passed,
        };
      });
    if (covenants.length === 0) { setError('Add at least one complete covenant test row'); return; }
    if (!evidenceRef.trim()) { setError('Source / evidence reference is required'); return; }

    setSubmitting(true);
    try {
      await axios.post('/api/credit/compliance-certificates/process', {
        loanSource: assetKind === 'LL' ? 'LEVERAGED_LOAN' : 'PRIVATE_CREDIT',
        loanId: position.loanRecordId,
        cusip: position.cusip || null,
        borrowerName: position.borrowerName,
        periodEndDate,
        sourceDocument: 'MANUAL_ENTRY',
        sourceReference: evidenceRef.trim(),
        covenants,
      });
      onSuccess();
    } catch (err: any) {
      setError(errMsg(err, 'Failed to record covenant test'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="Record Covenant Test"
      subtitle={subtitleFor(position)}
      icon={<ClipboardCheck size={18} className="text-cyan-400" />}
      onClose={onClose}
    >
      <Body>
        <LifecycleNotice tone="covenant" title="Operations action — covenant test">
          Enter the calculation components, not a pre-computed result. The server recomputes
          each actual, calculates cushion, fires alerts, and records this as manual UI evidence.
        </LifecycleNotice>

        <Field>
          <Label>Period End Date</Label>
          <DateField value={periodEndDate ?? ''} onChange={iso => setPeriodEndDate(iso)} />
        </Field>

        <Field>
          <Label>Source / Evidence Reference</Label>
          <Input type="text" value={evidenceRef}
            onChange={e => setEvidenceRef(e.target.value)}
            placeholder="e.g. borrower reporting pack, calculation workbook, or attestation ID" />
        </Field>

        <Field>
          <Label>Covenant Results</Label>
          {rows.map((r, i) => (
            <CovenantRowCard
              key={i}
              row={r}
              index={i}
              canRemove={rows.length > 1}
              onChange={patch => setRow(i, patch)}
              onRemove={() => removeRow(i)}
            />
          ))}
          <Button onClick={addRow} style={{ justifySelf: 'start', display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
            <Plus size={14} /> Add covenant
          </Button>
        </Field>

        {error && <ErrorBanner>{error}</ErrorBanner>}
      </Body>
      <Footer>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button $primary onClick={submit} disabled={submitting || !periodEndDate}>
          {submitting ? 'Submitting…' : 'Record Test'}
        </Button>
      </Footer>
    </ModalShell>
  );
};

// ============ PC INTEREST / FEE RECEIPT ============
//
// Structured-UI counterpart to the document-ingested PC interest/payment
// notice. It calls the same backend settlement service as ingestion, producing
// one cash-only COUPON trade plus interest/fee facts and lifecycle evidence.

const PcInterestFeeSettlementModal: React.FC<BaseProps> = ({ position, onClose, onSuccess }) => {
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState(today());
  const [effectiveDate, setEffectiveDate] = useState(today());
  const [interestAmount, setInterestAmount] = useState('');
  const [feeAmount, setFeeAmount] = useState('');
  const [evidenceRef, setEvidenceRef] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const interest = parseAmt(interestAmount) || 0;
  const fee = parseAmt(feeAmount) || 0;
  // F-PCP-031: what the record says is due for the period — funded principal × all-in rate × days / 360 (ACT/360,
  // the facility's convention) — so the receipt is a reconciliation of received against due, not a number typed blind.
  const periodDays = periodStart && periodEnd && periodEnd > periodStart
    ? Math.round((new Date(periodEnd + 'T00:00:00Z').getTime() - new Date(periodStart + 'T00:00:00Z').getTime()) / 86400000) : 0;
  const dueRate = position.allInRate ?? null;
  const duePerRecord = periodDays > 0 && dueRate != null && position.fundedAmount
    ? Math.round(position.fundedAmount * (dueRate / 100) * periodDays / 360 * 100) / 100 : null;
  const variance = duePerRecord != null && interest ? Math.round((interest - duePerRecord) * 100) / 100 : null;
  useEffect(() => {
    if (duePerRecord != null && !interestAmount) setInterestAmount(String(duePerRecord));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duePerRecord]);
  const total = interest + fee;

  const submit = async () => {
    setError(null);
    if (!periodStart || !periodEnd) { setError('Interest period start and end are required'); return; }
    if (periodStart >= periodEnd) { setError('Period start must be before period end'); return; }
    if (interest < 0 || fee < 0 || total <= 0) {
      setError('Enter a positive interest or commitment fee amount'); return;
    }
    if (!evidenceRef.trim()) { setError('Source / evidence reference is required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${loanBase('PC')}/${position.loanRecordId}/interest-settlement`, {
        periodStart,
        periodEnd,
        effectiveDate,
        interestAmount: interest,
        commitmentFeeAmount: fee,
        allInRatePct: position.allInRate ?? null,
        currentFace: position.fundedAmount ?? null,
        evidenceRef: evidenceRef.trim(),
        // F-PCP-031: the receipt records what the record said was due and the variance, beside what arrived.
        notes: [notes.trim(), duePerRecord != null ? `Due per record ${duePerRecord.toFixed(2)} (${periodDays}d ACT/360 at ${Number(dueRate).toFixed(2)}%)${variance != null && Math.abs(variance) >= 0.01 ? `; variance ${variance.toFixed(2)}` : ''}` : ''].filter(Boolean).join(' · ') || null,
      });
      onSuccess();
    } catch (err: any) {
      setError(errMsg(err, 'Failed to record interest/fee receipt'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="Record Interest / Fee Receipt"
      subtitle={subtitleFor(position)}
      icon={<Percent size={18} className="text-emerald-400" />}
      onClose={onClose}
    >
      <Body>
        <LifecycleNotice tone="paydown" title="Operations action — position cash receipt">
          Records received interest and commitment fees through the same governed settlement path
          as an ingested payment notice. The receipt posts as a cash-only COUPON trade and never
          changes principal.
        </LifecycleNotice>

        <FieldRow>
          <Field>
            <Label>Interest Period Start</Label>
            <DateField value={periodStart ?? ''} onChange={iso => setPeriodStart(iso)} />
          </Field>
          <Field>
            <Label>Interest Period End</Label>
            <DateField value={periodEnd ?? ''} onChange={iso => setPeriodEnd(iso)} />
          </Field>
          <Field>
            <Label>Cash Effective Date</Label>
            <DateField value={effectiveDate ?? ''} onChange={iso => setEffectiveDate(iso)} />
          </Field>
        </FieldRow>

        <FieldRow>
          <Field>
            <Label>Interest Received ($)</Label>
            <Input type="text" inputMode="decimal" value={interestAmount}
              onChange={e => setInterestAmount(e.target.value)} placeholder="e.g. 1.25MM" />
            <AmountHint raw={interestAmount} />
          </Field>
          <Field>
            <Label>Commitment Fee Received ($) — optional</Label>
            <Input type="text" inputMode="decimal" value={feeAmount}
              onChange={e => setFeeAmount(e.target.value)} placeholder="e.g. 125,000" />
            <AmountHint raw={feeAmount} />
          </Field>
        </FieldRow>

        {duePerRecord != null && <PreviewCard tone="drawdown" title="Due per the record">
          {fmtCcy(duePerRecord)} — {periodDays} days ACT/360 on {fmtCcy(position.fundedAmount)} at {Number(dueRate).toFixed(2)}%.
          {variance != null && Math.abs(variance) >= 0.01 ? ` Received differs by ${variance > 0 ? '+' : ''}${fmtCcy(variance)}; the variance is recorded with the receipt.` : variance != null ? ' Received matches.' : ' Enter the amount received to compare.'}
        </PreviewCard>}
        <PreviewCard tone="paydown" title="Cash receipt preview">
          Interest {fmtCcy(interest)} + commitment fee {fmtCcy(fee)} ={' '}
          <strong style={{ color: '#e2e8f0' }}>{fmtCcy(total)}</strong> cash posted to IBOR.
          Principal remains {fmtCcy(position.fundedAmount)}.
        </PreviewCard>

        <Field>
          <Label>Source / Evidence Reference</Label>
          <Input type="text" value={evidenceRef}
            onChange={e => setEvidenceRef(e.target.value)}
            placeholder="e.g. agent portal receipt, wire reference, or manual attestation ID" />
        </Field>
        <Field>
          <Label>Notes — optional</Label>
          <Input type="text" value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Operational context or reconciliation note" />
        </Field>

        {error && <ErrorBanner>{error}</ErrorBanner>}
      </Body>
      <Footer>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button $primary onClick={submit} disabled={submitting || !(periodStart && periodEnd && effectiveDate)}>
          {submitting ? 'Posting…' : 'Confirm Receipt'}
        </Button>
      </Footer>
    </ModalShell>
  );
};

// ============ LL INTEREST / FEE NOTICE (ops tranche-event, AXP-145 Slice 1e Part B) ============
//
// An agent Interest/Fee notice is a FACILITY-level event: one INTEREST tranche
// event settles each settled holder's interest receivable PLUS its commitment/
// ticking fee leg on the SAME quarterly payment (Option C recompute-and-pay).
// Interest never moves principal; the fee books as a SEPARATE FEE cash-flow.
// Both the stated interest and the stated fee are OPTIONAL — when supplied the
// engine reconciles the computed amount against them (tolerance), otherwise it
// simply computes and books. Posts to the permissioned ops tranche-event
// endpoint (operations-only; the agent-notice document pipeline is the primary
// feed). LL-only.

const LlInterestFeeNoticeModal: React.FC<BaseProps> = ({ position, onClose, onSuccess }) => {
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState(today());
  const [statedInterest, setStatedInterest] = useState('');
  const [statedFee, setStatedFee] = useState('');
  const [evidenceRef, setEvidenceRef] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (!periodStart || !periodEnd) { setError('Interest period start and end are required'); return; }
    if (periodStart >= periodEnd) { setError('Period start must be before period end'); return; }
    if (!evidenceRef.trim()) { setError('Agent evidence reference is required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${LL_TRANCHE_EVENTS_BASE}/tranches/${position.loanRecordId}/interest`, {
        periodStart,
        periodEnd,
        // Optional stated legs — null means "compute and book without reconciliation".
        statedInterestAmount: statedInterest.trim() ? parseAmt(statedInterest) : null,
        statedFeeAmount: statedFee.trim() ? parseAmt(statedFee) : null,
        evidenceRef: evidenceRef.trim() || null,
      });
      onSuccess();
    } catch (err: any) {
      setError(errMsg(err, 'Failed to record interest/fee notice'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="Record Interest / Fee Notice"
      subtitle={subtitleFor(position)}
      icon={<Percent size={18} className="text-emerald-400" />}
      onClose={onClose}
    >
      <Body>
        <LifecycleNotice tone="paydown" title="Operations action — facility-level interest / fee">
          Settles each settled holder's interest receivable and, on the same quarterly payment,
          its commitment/ticking fee as a separate FEE cash-flow (AXP-145). Interest never moves
          principal. Stated amounts are optional — supplied figures are reconciled within tolerance.
        </LifecycleNotice>

        <FieldRow>
          <Field>
            <Label>Interest Period Start</Label>
            <DateField value={periodStart ?? ''} onChange={iso => setPeriodStart(iso)} />
          </Field>
          <Field>
            <Label>Interest Period End</Label>
            <DateField value={periodEnd ?? ''} onChange={iso => setPeriodEnd(iso)} />
          </Field>
        </FieldRow>

        <FieldRow>
          <Field>
            <Label>Stated Interest ($) — optional</Label>
            <Input type="text" inputMode="decimal" value={statedInterest}
              onChange={e => setStatedInterest(e.target.value)} placeholder="e.g. 3645833.33" />
            <AmountHint raw={statedInterest} />
          </Field>
          <Field>
            <Label>Stated Fee ($) — optional</Label>
            <Input type="text" inputMode="decimal" value={statedFee}
              onChange={e => setStatedFee(e.target.value)} placeholder="commitment / ticking fee" />
            <AmountHint raw={statedFee} />
          </Field>
        </FieldRow>

        <Field>
          <Label>Agent Evidence Ref</Label>
          <Input type="text" value={evidenceRef}
            onChange={e => setEvidenceRef(e.target.value)} placeholder="e.g. BofA-INT-HTPK-20260720" />
        </Field>

        {error && <ErrorBanner>{error}</ErrorBanner>}
      </Body>
      <Footer>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button $primary onClick={submit} disabled={submitting || !(periodStart && periodEnd)}>
          {submitting ? 'Applying…' : 'Confirm Interest / Fee'}
        </Button>
      </Footer>
    </ModalShell>
  );
};

// ============ LL PIK CAPITALIZATION (ops tranche-event, FRD Phase 2 Slice 7) ============
//
// On a PIK date, roll each settled holder's accrued-but-un-capitalized daily PIK
// interest into principal via a NON-CASH par-cost capitalization lot. No
// settlement cash — PIK is paid in kind. Idempotent per PIK date (a replay for a
// date <= the pik_capitalized_to_date watermark is a no-op).
const LlPikCapitalizeModal: React.FC<BaseProps> = ({ position, onClose, onSuccess }) => {
  const [pikDate, setPikDate] = useState(today());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (!pikDate) { setError('PIK capitalization date is required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${LL_TRANCHE_EVENTS_BASE}/tranches/${position.loanRecordId}/pik-capitalize`, { pikDate });
      onSuccess();
    } catch (err: any) {
      setError(errMsg(err, 'Failed to capitalize PIK'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="Capitalize PIK"
      subtitle={subtitleFor(position)}
      icon={<Layers size={18} className="text-violet-400" />}
      onClose={onClose}
    >
      <Body>
        <LifecycleNotice tone="amend" title="Operations action — non-cash PIK capitalization">
          Rolls each settled holder's accrued daily PIK interest up to and including this date
          into funded principal. No settlement cash. Idempotent: re-running for the same or an
          earlier date is a no-op.
        </LifecycleNotice>

        <Field>
          <Label>PIK Capitalization Date</Label>
          <DateField value={pikDate ?? ''} onChange={iso => setPikDate(iso)} />
        </Field>

        {error && <ErrorBanner>{error}</ErrorBanner>}
      </Body>
      <Footer>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button $primary onClick={submit} disabled={submitting || !pikDate}>
          {submitting ? 'Capitalizing…' : 'Capitalize PIK'}
        </Button>
      </Footer>
    </ModalShell>
  );
};

// ============ LL PIK ELECTION (ops tranche-event, FRD Phase 2 Slice 7) ============
//
// PIK-toggle facilities let the borrower elect, per interest period, whether the
// coupon accrues in CASH or is PAID IN KIND. The election is a facility-level
// term governing how the daily accrual books over [periodStart, periodEnd]. One
// election per period — a replay for a period that already has an election is
// refused server-side with 409. LL-only.
const LlPikElectionModal: React.FC<BaseProps> = ({ position, onClose, onSuccess }) => {
  const [election, setElection] = useState('PIK');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState(today());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (!periodStart || !periodEnd) { setError('Election period start and end are required'); return; }
    if (periodStart >= periodEnd) { setError('Period start must be before period end'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${LL_TRANCHE_EVENTS_BASE}/tranches/${position.loanRecordId}/pik-election`, {
        election,
        periodStart,
        periodEnd,
      });
      onSuccess();
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setError('An election already exists for this period.');
      } else {
        setError(errMsg(err, 'Failed to record PIK election'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="PIK Election"
      subtitle={subtitleFor(position)}
      icon={<Percent size={18} className="text-violet-400" />}
      onClose={onClose}
    >
      <Body>
        <LifecycleNotice tone="amend" title="Operations action — per-period PIK election">
          Records whether the coupon for this interest period accrues in cash or is paid in kind.
          The daily accrual for the period books accordingly. One election per period — an election
          that already exists for the period is refused.
        </LifecycleNotice>

        <Field>
          <Label>Election</Label>
          <Select value={election} onChange={e => setElection(e.target.value)}>
            <option value="CASH_PAY">Cash Pay</option>
            <option value="PIK">PIK (Paid In Kind)</option>
          </Select>
        </Field>

        <FieldRow>
          <Field>
            <Label>Period Start</Label>
            <DateField value={periodStart ?? ''} onChange={iso => setPeriodStart(iso)} />
          </Field>
          <Field>
            <Label>Period End</Label>
            <DateField value={periodEnd ?? ''} onChange={iso => setPeriodEnd(iso)} />
          </Field>
        </FieldRow>

        {error && <ErrorBanner>{error}</ErrorBanner>}
      </Body>
      <Footer>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button $primary onClick={submit} disabled={submitting || !(periodStart && periodEnd)}>
          {submitting ? 'Recording…' : 'Record Election'}
        </Button>
      </Footer>
    </ModalShell>
  );
};

// ============ PC PIK CAPITALIZATION (accrued PIK → principal) ============
//
// Capitalize this private credit loan's accrued-but-un-capitalized daily PIK
// interest into funded principal via a NON-CASH par-cost capitalization lot on
// the PIK date. No settlement cash — PIK is paid in kind. Idempotent per PIK date
// (a replay for a date <= the capitalized-to watermark is a no-op). PC-only.
const PcPikCapitalizeModal: React.FC<BaseProps> = ({ position, onClose, onSuccess }) => {
  const [pikDate, setPikDate] = useState(today());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (!pikDate) { setError('PIK capitalization date is required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`/api/private-credit/loans/${position.loanRecordId}/pik-capitalize-accrued`, { pikDate });
      onSuccess();
    } catch (err: any) {
      setError(errMsg(err, 'Failed to capitalize PIK'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="Capitalize PIK"
      subtitle={subtitleFor(position)}
      icon={<Layers size={18} className="text-violet-400" />}
      onClose={onClose}
    >
      <Body>
        <LifecycleNotice tone="amend" title="Operations action — non-cash PIK capitalization">
          Rolls this private credit loan's accrued daily PIK interest up to and including this date
          into funded principal. No settlement cash. Idempotent: re-running for the same or an
          earlier date is a no-op.
        </LifecycleNotice>

        <Field>
          <Label>PIK Capitalization Date</Label>
          <DateField value={pikDate ?? ''} onChange={iso => setPikDate(iso)} />
        </Field>

        {error && <ErrorBanner>{error}</ErrorBanner>}
      </Body>
      <Footer>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button $primary onClick={submit} disabled={submitting || !pikDate}>
          {submitting ? 'Capitalizing…' : 'Capitalize PIK'}
        </Button>
      </Footer>
    </ModalShell>
  );
};

// ============ PC PIK ELECTION (per-period cash-pay vs PIK) ============
//
// PIK-toggle private credit facilities let the borrower elect, per interest
// period, whether the coupon accrues in CASH or is PAID IN KIND. The election is
// a term governing how the daily accrual books over [periodStart, periodEnd]. One
// election per period — a replay for a period that already has an election is
// refused server-side with 409. PC-only.
const PcPikElectionModal: React.FC<BaseProps> = ({ position, onClose, onSuccess }) => {
  const [election, setElection] = useState('PIK');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState(today());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (!periodStart || !periodEnd) { setError('Election period start and end are required'); return; }
    if (periodStart >= periodEnd) { setError('Period start must be before period end'); return; }
    setSubmitting(true);
    try {
      await axios.post(`/api/private-credit/loans/${position.loanRecordId}/pik-election`, {
        election,
        periodStart,
        periodEnd,
      });
      onSuccess();
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setError('An election already exists for this period.');
      } else {
        setError(errMsg(err, 'Failed to record PIK election'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="PIK Election"
      subtitle={subtitleFor(position)}
      icon={<Percent size={18} className="text-violet-400" />}
      onClose={onClose}
    >
      <Body>
        <LifecycleNotice tone="amend" title="Operations action — per-period PIK election">
          Records whether the coupon for this private credit interest period accrues in cash or is
          paid in kind. The daily accrual for the period books accordingly. One election per period —
          an election that already exists for the period is refused.
        </LifecycleNotice>

        <Field>
          <Label>Election</Label>
          <Select value={election} onChange={e => setElection(e.target.value)}>
            <option value="CASH_PAY">Cash Pay</option>
            <option value="PIK">PIK (Paid In Kind)</option>
          </Select>
        </Field>

        <FieldRow>
          <Field>
            <Label>Period Start</Label>
            <DateField value={periodStart ?? ''} onChange={iso => setPeriodStart(iso)} />
          </Field>
          <Field>
            <Label>Period End</Label>
            <DateField value={periodEnd ?? ''} onChange={iso => setPeriodEnd(iso)} />
          </Field>
        </FieldRow>

        {error && <ErrorBanner>{error}</ErrorBanner>}
      </Body>
      <Footer>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button $primary onClick={submit} disabled={submitting || !(periodStart && periodEnd)}>
          {submitting ? 'Recording…' : 'Record Election'}
        </Button>
      </Footer>
    </ModalShell>
  );
};

export type CreditActionKind =
  | 'paydown' | 'drawdown' | 'rate-reset' | 'amend' | 'writedown' | 'payoff' | 'covenant-test' | 'interest'
  | 'pik-capitalize' | 'pik-election'
  | 'pc-pik-capitalize' | 'pc-pik-election';

/** Render the modal for a given action kind. Returns null for unknown kinds. */
export const CreditActionModal: React.FC<BaseProps & { kind: CreditActionKind }> = ({ kind, ...props }) => {
  switch (kind) {
    case 'paydown': return <PcPaydownModal {...props} />;
    case 'drawdown': return <PcDrawdownModal {...props} />;
    case 'rate-reset': return <PcRateResetModal {...props} />;
    case 'amend': return <PcAmendModal {...props} />;
    case 'writedown': return <PcWritedownModal {...props} />;
    case 'payoff': return <PcPayoffModal {...props} />;
    case 'covenant-test': return <PcCovenantTestModal {...props} />;
    case 'interest': return props.assetKind === 'PC'
      ? <PcInterestFeeSettlementModal {...props} />
      : <LlInterestFeeNoticeModal {...props} />;
    case 'pik-capitalize': return <LlPikCapitalizeModal {...props} />;
    case 'pik-election': return <LlPikElectionModal {...props} />;
    // PC PIK actions — only meaningful when assetKind==='PC'.
    case 'pc-pik-capitalize': return <PcPikCapitalizeModal {...props} />;
    case 'pc-pik-election': return <PcPikElectionModal {...props} />;
    default: return null;
  }
};
